// src/sim/content/cache.ts — Module-level cache holding the hydrated ContentBundle.
//
// Pattern mirrors src/sim/ai/p4-caches.ts: private `let` state + exported
// hydrate/get/reset trio. Matches P5 D-23 (getProfile + getCommentary body-swap):
// hydrate is called ONCE at App boot (D-24) before character-select mounts;
// every subsequent sim tick reads through getContentBundle().
//
// P6 additions (06-01-PLAN.md Task 2):
//   - getQuote(characterId, eventTag, lineIndex?) reader (D-04)
//   - getVoicePitch(characterId) reader (D-06)
//   - getQuietness(characterId) reader (D-09)
//   - pickLineIndex(characterId, eventTag, rngFn, totalLines) — DIAL-08
//     dialogue-fatigue weighting via recentUseCount: weight = 1/(1 + count).
//     Caller passes `rng.waffle` (P0 sub-seed) for replay determinism.
//   - decayDialogueFatigue() — called by 06-03 audio manager subscriber on
//     'round-started'; decrements counts by 1 and removes entries that
//     reach 0 so a fresh round resets the texture.
//
// Source: .planning/phases/05-content-pipeline-characters-admin-cms/05-CONTEXT.md D-23, D-24.
//         .planning/phases/06-dialogue-audio/06-CONTEXT.md D-04, D-06, D-08, D-09.

import type { ContentBundle } from '../../shared/content/types';
import type { EventTag } from '../../shared/types/events';
import { registerP4ResetCallback } from '../ai/p4-caches';
import {
  P3_FOXGUARD_RECENT_DROPOFF_TICKS,
  P3_FOXGUARD_NEAR_BASE_TILES,
  P3_FOXGUARD_VELOCITY_PX_PER_S,
  P3_FOXGUARD_GRACE_TICKS,
  P3_FOXGUARD_SHOUT_COOLDOWN_TICKS,
} from '../foxguard/constants';
import {
  P3_ROUND_DURATION_TICKS,
  P3_HEADSTART_DURATION_TICKS,
  P3_TEAMSWITCH_DURATION_TICKS,
} from '../round/constants';
import {
  P4_AWARENESS_SUSPICIOUS,
  P4_AWARENESS_ALERTED,
  P4_AWARENESS_SPOTTED,
  P4_AWARENESS_BASE_RATE,
  P4_AWARENESS_DECAY_RATE,
  P4_AWARENESS_MAX_RANGE_TILES,
} from '../ai/awareness/constants';
import {
  P4_BLACKBOARD_SIGMA_GROWTH_PER_TICK,
  P4_BLACKBOARD_MAX_AGE_TICKS,
  P4_SHOUT_WALL_PENALTY_TILES,
  P4_INVESTIGATE_BASE,
  P4_INVESTIGATE_DISTANCE_WEIGHT,
  P4_INVESTIGATE_DUPLICATE_WEIGHT,
  P4_INVESTIGATE_CONFIDENCE_WEIGHT,
} from '../ai/coordination/constants';

let cached: ContentBundle | null = null;

/**
 * Install the live ContentBundle. Called exactly once at App boot by
 * src/ui/useContent.ts (Plan 06 wires this); test code may call it with
 * a fixture bundle.
 */
export function hydrateContent(bundle: ContentBundle): void {
  cached = bundle;
  // Invalidate the resolved-tuning memo so the next getTuning() rebuilds from
  // the freshly-hydrated bundle (content hot-reloads at round start — ADMIN-08).
  resolvedTuning = null;
}

/**
 * Read the hydrated bundle. Throws if called before hydrateContent —
 * every call site (getProfile / getCommentary) relies on the boot order
 * contract from D-24.
 */
export function getContentBundle(): ContentBundle {
  if (!cached) {
    throw new Error(
      'Content not hydrated. hydrateContent(bundle) must run before any sim tick or render call.',
    );
  }
  return cached;
}

/** Cheap presence check; used by DevOverlay to gate render branches. */
export function hasContentBundle(): boolean {
  return cached !== null;
}

// ===========================================================================
// P7 POL-06 / D-12 tuning reader (fallback-on-miss)
// ===========================================================================
//
// Mirrors the getVoicePitch fallback-on-miss template: an absent `tuning`
// section (or a missing field within it) resolves to the sim code constant.
// TUNING_DEFAULTS imports those constants directly so the fallback is provably
// equal to the baked default — the load-bearing invariant that keeps the
// golden-hash determinism test unchanged (Pitfall B). The only field NOT sourced
// from a constants file is quietnessHearingMult: its sole consumer was a private
// const in perception.ts, now collapsed here as the single source of truth.

/** Fully-resolved tuning shape (every field present). Inferred from the schema's
 *  optional `tuning` object, with undefined stripped — keeps field names in lockstep
 *  with ContentBundleSchema so a schema rename forces a compile error here. */
export type ResolvedTuning = NonNullable<ContentBundle['tuning']>;

/** Code-constant defaults. Each value is imported from the owning sim constants
 *  file so a future constant change cannot silently drift the fallback. */
export const TUNING_DEFAULTS: ResolvedTuning = {
  foxguardRecentDropoffTicks: P3_FOXGUARD_RECENT_DROPOFF_TICKS,
  foxguardNearBaseTiles: P3_FOXGUARD_NEAR_BASE_TILES,
  foxguardVelocityPxPerS: P3_FOXGUARD_VELOCITY_PX_PER_S,
  foxguardGraceTicks: P3_FOXGUARD_GRACE_TICKS,
  foxguardShoutCooldownTicks: P3_FOXGUARD_SHOUT_COOLDOWN_TICKS,
  roundDurationTicks: P3_ROUND_DURATION_TICKS,
  headstartDurationTicks: P3_HEADSTART_DURATION_TICKS,
  teamswitchDurationTicks: P3_TEAMSWITCH_DURATION_TICKS,
  awarenessSuspicious: P4_AWARENESS_SUSPICIOUS,
  awarenessAlerted: P4_AWARENESS_ALERTED,
  awarenessSpotted: P4_AWARENESS_SPOTTED,
  awarenessBaseRate: P4_AWARENESS_BASE_RATE,
  awarenessDecayRate: P4_AWARENESS_DECAY_RATE,
  awarenessMaxRangeTiles: P4_AWARENESS_MAX_RANGE_TILES,
  blackboardSigmaGrowthPerTick: P4_BLACKBOARD_SIGMA_GROWTH_PER_TICK,
  blackboardMaxAgeTicks: P4_BLACKBOARD_MAX_AGE_TICKS,
  shoutWallPenaltyTiles: P4_SHOUT_WALL_PENALTY_TILES,
  investigateBase: P4_INVESTIGATE_BASE,
  investigateDistanceWeight: P4_INVESTIGATE_DISTANCE_WEIGHT,
  investigateDuplicateWeight: P4_INVESTIGATE_DUPLICATE_WEIGHT,
  investigateConfidenceWeight: P4_INVESTIGATE_CONFIDENCE_WEIGHT,
  // P6: hearing modulation pinned at 1 until playtest (was a private const in
  // perception.ts; this is now its single source of truth). TODO(P7 POL-06): retune.
  quietnessHearingMult: 1,
};

// Memoised resolved tuning. Built once per hydrated bundle and reused so the
// hot AI loops (awareness accumulator, blackboard, utility scorers) that call
// getTuning() per-entity / per-entry do not allocate a fresh object each call
// (Pitfall 11 allocation discipline). Cleared by hydrateContent + reset.
let resolvedTuning: ResolvedTuning | null = null;

/**
 * P7 POL-06 / D-12: resolve sim tuning with the code constant as the exact
 * fallback default. Pre-hydrate (or when the bundle omits `tuning`) returns the
 * code-constant defaults; a present `tuning` section overrides per field.
 *
 * Never throws — guards on hasContentBundle() so sim code paths that may run
 * before boot hydration (defensive) still get sane defaults.
 */
export function getTuning(): ResolvedTuning {
  if (resolvedTuning) return resolvedTuning;
  if (!hasContentBundle()) {
    // Pre-hydrate: return defaults WITHOUT memoising, so a later hydrate with an
    // overriding `tuning` section still takes effect.
    return TUNING_DEFAULTS;
  }
  const t = getContentBundle().tuning;
  resolvedTuning = t ? { ...TUNING_DEFAULTS, ...t } : TUNING_DEFAULTS;
  return resolvedTuning;
}

// ===========================================================================
// P6 D-04 / D-06 / D-08 / D-09 readers
// ===========================================================================

/**
 * P6 D-04: read a single line from the authored quote bucket for a
 * (characterId, eventTag) pair. Returns null when no quote is authored or
 * the bucket is empty; clamps an out-of-range lineIndex to the last line so
 * callers don't have to guard the bound.
 */
export function getQuote(
  characterId: string,
  eventTag: EventTag,
  lineIndex: number = 0,
): string | null {
  const bundle = getContentBundle();
  const quote = bundle.quotes.find(
    (q) => q.characterId === characterId && q.eventTag === eventTag,
  );
  if (!quote || quote.lines.length === 0) return null;
  const idx = Math.min(Math.max(0, lineIndex), quote.lines.length - 1);
  return quote.lines[idx] ?? null;
}

/**
 * P6 D-06: read the per-character pitch multiplier for waffle Howl.rate.
 * Defaults to 1.0 on miss so callers never crash on an unknown id —
 * 06-03 audio manager prefers a quiet fallback over a hard fail.
 */
export function getVoicePitch(characterId: string): number {
  const bundle = getContentBundle();
  const c = bundle.characters.find((ch) => ch.id === characterId);
  return c?.voicePitch ?? 1.0;
}

/**
 * P6 D-09: read the per-character moving + hiding loudness modulators.
 * Defaults to {0.5, 0.5} on miss; render-only consumption in P6 (sim hearing
 * is gated at multiplier=1 by P6_QUIETNESS_HEARING_MULT in perception.ts).
 */
export function getQuietness(characterId: string): { move: number; hide: number } {
  const bundle = getContentBundle();
  const c = bundle.characters.find((ch) => ch.id === characterId);
  return {
    move: c?.move.quietness ?? 0.5,
    hide: c?.hide.quietness ?? 0.5,
  };
}

// ===========================================================================
// P6 D-08 dialogue-fatigue line picker
// ===========================================================================
//
// Per (characterId, eventTag) pair, track how often each lineIndex has been
// chosen recently. The picker draws an index with weight = 1 / (1 + count).
// `decayDialogueFatigue()` decrements every count by 1 and removes entries
// that hit 0 — invoked by the audio manager renderBus 'round-started'
// subscriber (06-03) so a fresh round restores uniform weighting.
//
// State is module-level (matches the cached bundle's lifecycle); reset by
// resetContentCacheForTests() so Vitest specs don't leak between cases.

const recentUseCount: Map<string, Map<number, number>> = new Map();

/**
 * Replay-equality determinism guard: clear recentUseCount whenever a new
 * simulation run begins (createInitialWorldState). Without this, two
 * back-to-back runs with the same seed would diverge — run 2's first
 * pickLineIndex draw would weight against indices that run 1 had already
 * touched, producing a different lineIndex sequence in events[]. Mirrors
 * how src/sim/tick.ts WR-06 wires resetRngCacheForNewSim through the same
 * hub. (Within a single round, decay still happens via decayDialogueFatigue
 * called by the 06-03 audio manager subscriber on round-started.)
 */
registerP4ResetCallback(() => {
  recentUseCount.clear();
});

/**
 * P6 D-08 dialogue-fatigue weighted picker.
 *
 * Caller MUST pass `rng.waffle` (P0-reserved sub-seed) so the chosen line is
 * deterministic across replay. Returns -1 when totalLines is 0 (caller
 * no-op contract — used by tick.ts maybeEmitCharacterSpoke to skip emission
 * when no quote is authored).
 *
 * On success the chosen index's count is incremented in `recentUseCount`.
 *
 * Floating-point fallback: if the cumulative weight loop never crosses the
 * threshold (rng==1.0, total drift), we attribute the pick to the last
 * index — guarantees a valid return value rather than -1.
 */
export function pickLineIndex(
  characterId: string,
  eventTag: EventTag,
  rngFn: () => number,
  totalLines: number,
): number {
  if (totalLines <= 0) return -1;
  const key = `${characterId}:${eventTag}`;
  let countMap = recentUseCount.get(key);
  if (!countMap) {
    countMap = new Map();
    recentUseCount.set(key, countMap);
  }
  const weights: number[] = [];
  let total = 0;
  for (let i = 0; i < totalLines; i++) {
    const w = 1 / (1 + (countMap.get(i) ?? 0));
    weights.push(w);
    total += w;
  }
  let r = rngFn() * total;
  for (let i = 0; i < totalLines; i++) {
    r -= weights[i]!;
    if (r <= 0) {
      countMap.set(i, (countMap.get(i) ?? 0) + 1);
      return i;
    }
  }
  // Floating-point fallback — last index.
  const last = totalLines - 1;
  countMap.set(last, (countMap.get(last) ?? 0) + 1);
  return last;
}

/**
 * P6 D-08 decay step. Decrements every recentUseCount by 1; entries that
 * hit 0 are removed (so a long-cold line resets to weight=1, not weight<1).
 * Invoked by the 06-03 audio manager renderBus 'round-started' subscriber.
 */
export function decayDialogueFatigue(): void {
  for (const countMap of recentUseCount.values()) {
    for (const [lineIdx, count] of [...countMap.entries()]) {
      if (count <= 1) countMap.delete(lineIdx);
      else countMap.set(lineIdx, count - 1);
    }
  }
}

/** TEST-ONLY reset between Vitest specs. Not called from production code. */
export function resetContentCacheForTests(): void {
  cached = null;
  resolvedTuning = null;
  recentUseCount.clear();
}
